import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { agentApi, useAgentSettings, type AgentRun, type DetailSection, type Rec } from '../../../../api/visitorsAgent'
import { ApiError } from '../../../../api/client'
import { btn, btnPrimary, card, ErrorLine, h2, inp } from '../../../../campaigns-desk/ui'
import { pubLabel } from '../../../../lib/deskFormat'
import { fmtDateTime } from '../../../../lib/format'
import { errorDetail } from '../../../../lib/visitorsFormat'
import { directionLabel, groupLabel, isLivePending, isStalePending, safeDeskHref, STALE_RUN_TEXT } from '../../../../lib/visitorsAgent'
import { ChatPanel } from './ChatPanel'
import { Md } from './Md'
import { RecommendationCard } from './RecommendationCard'

const DIR_TONE = { up: 'text-radar-green', down: 'text-radar-red', flat: 'text-paper-dim' } as const
const DIR_MARK = { up: '▲', down: '▼', flat: '▬' } as const
const chip = 'inline-flex shrink-0 rounded-full bg-ink-600 px-2.5 py-0.5 text-[12px] font-semibold text-paper-dim ring-1 ring-ink-400'

function writtenLine(r: AgentRun): string {
  const parts = [`Written ${r.finished_at ? fmtDateTime(r.finished_at) : r.created_at ? fmtDateTime(r.created_at) : ''}`.trim()]
  if (r.model) parts.push(r.model)
  if (r.cost_cents != null) parts.push(`$${(r.cost_cents / 100).toFixed(2)}`)
  return parts.join(' · ')
}

function Section({ s }: { s: DetailSection }) {
  const [open, setOpen] = useState(true)
  return (
    <section className={`${card} space-y-2`}>
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-3 text-left">
        <span className="flex flex-wrap items-center gap-2">
          <span className={chip}>{groupLabel(s.group)}</span>
          <span className="font-serif text-[18px] font-medium text-paper">{s.heading}</span>
        </span>
        <span aria-hidden="true" className="text-paper-faint">{open ? '−' : '+'}</span>
      </button>
      {open && <Md text={s.body} />}
    </section>
  )
}

/** `visitors/desk/:pub/summary`: the analyst's morning summary, recommendations and chat. */
export function SummaryPage() {
  const { pub = 'all' } = useParams()
  return <SummaryBody key={pub} pub={pub} />
}

function SummaryBody({ pub }: { pub: string }) {
  const qc = useQueryClient()
  const settings = useAgentSettings()
  const [picked, setPicked] = useState<number | null>(null)
  const [conv, setConv] = useState<number | null>(null)

  const latest = useQuery({
    queryKey: ['vd-agent-latest', pub],
    queryFn: () => agentApi.latest(pub),
    enabled: settings.data != null,
    refetchInterval: (q) => (isLivePending(q.state.data?.active) ? 3000 : false),
  })
  const past = useQuery({ queryKey: ['vd-agent-runs', pub], queryFn: () => agentApi.runs(pub), enabled: settings.data != null })
  const convs = useQuery({ queryKey: ['vd-agent-conversations', pub], queryFn: () => agentApi.conversations(pub), enabled: settings.data != null })

  const active = latest.data?.active ?? null
  // Past 15 minutes a queued/running run is given up on: polling stops and the page says so.
  const stale = isStalePending(active)
  const writing = isLivePending(active)
  const runId = picked ?? latest.data?.run?.id ?? null
  const full = useQuery({ queryKey: ['vd-agent-run', runId], queryFn: () => agentApi.run(runId as number), enabled: runId != null })

  // The active run finished: pick up the new summary.
  const wasActive = useRef(false)
  useEffect(() => {
    if (wasActive.current && !active) {
      void qc.invalidateQueries({ queryKey: ['vd-agent-runs', pub] })
      void qc.invalidateQueries({ queryKey: ['vd-agent-run'] })
    }
    wasActive.current = !!active
  }, [active, pub, qc])

  const refresh = useMutation({
    mutationFn: () => agentApi.queueRun(pub),
    onSuccess: (r) => {
      setPicked(null)
      qc.setQueryData(['vd-agent-latest', pub], (old: { run: AgentRun | null; active: AgentRun | null } | undefined) => ({ run: old?.run ?? null, active: r }))
    },
    // 409: a run is already queued or running; the refetch finds it and polling follows it.
    onError: (e) => { if (e instanceof ApiError && e.status === 409) void qc.invalidateQueries({ queryKey: ['vd-agent-latest', pub] }) },
  })

  const discuss = useMutation({
    mutationFn: (rec: Rec) => agentApi.createConversation({ scope: pub, run_id: full.data?.id, recommendation_id: rec.id }),
    onSuccess: (c) => { setConv(c.id); void qc.invalidateQueries({ queryKey: ['vd-agent-conversations', pub] }) },
  })

  if (settings.isPending) return null
  if (settings.isError) return <p role="alert" className="text-radar-red">Couldn't reach the analyst.</p>
  if (settings.data == null) return <p className="text-paper-dim">The analyst is switched off.</p>

  const run = full.data
  const shown = run && run.status === 'done' ? run : null
  const newest = past.data?.runs[0]
  const failedBanner = !writing && picked == null && newest?.status === 'failed'
    && (!shown || new Date(newest.created_at ?? 0).getTime() > new Date(shown.created_at ?? 0).getTime()) ? newest : null
  const refreshError = refresh.isError && !(refresh.error instanceof ApiError && refresh.error.status === 409) ? errorDetail(refresh.error) : null

  return (
    <div className="max-w-[1500px] space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-[28px] text-paper">Summary</h1>
          <p className="text-[13px] text-paper-faint">{pubLabel(pub)}{shown ? ` · ${shown.period_start} to ${shown.period_end}` : ''}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(past.data?.runs.filter((r) => r.status === 'done').length ?? 0) > 0 && (
            <label className="flex items-center gap-2 text-[13px] text-paper-dim">
              Past summaries
              <select className={inp} value={picked ?? ''} onChange={(e) => setPicked(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Latest</option>
                {past.data?.runs.filter((r) => r.status === 'done').map((r) => (
                  <option key={r.id} value={r.id}>{fmtDateTime(r.created_at ?? '')} · {r.kind}</option>
                ))}
              </select>
            </label>
          )}
          <button type="button" className={btnPrimary} disabled={writing || refresh.isPending}
            onClick={() => { if (!writing && !refresh.isPending) refresh.mutate() }}>{writing || refresh.isPending ? 'Writing…' : 'Refresh'}</button>
        </div>
      </div>
      <ErrorLine text={refreshError} />

      <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-5">
          {(latest.isPending || (runId != null && full.isPending)) && <p className="text-paper-faint">Loading…</p>}
          {latest.isError && <ErrorLine text={errorDetail(latest.error)} />}
          {writing && <p role="status" className="text-[13px] text-paper-dim">The analyst is writing a new summary. This page updates by itself.</p>}
          {stale && <p role="alert" className="text-[13px] text-radar-red">{STALE_RUN_TEXT}</p>}
          {!writing && !stale && latest.data && !latest.data.run && !failedBanner && <p className="text-paper-dim">No summary yet. Use Refresh to write one.</p>}
          {failedBanner && (
            <div className={`${card} space-y-2`}>
              <p role="alert" className="text-[14px] text-radar-red">{failedBanner.error || 'The summary failed.'}</p>
              {shown && <p className="text-[13px] text-paper-faint">Showing the last good summary from {fmtDateTime(shown.finished_at ?? shown.created_at)}</p>}
              <button type="button" className={btn} disabled={writing || refresh.isPending} onClick={() => refresh.mutate()}>Try again</button>
            </div>
          )}
          {shown && (
            <>
              <section className={`${card} space-y-3`} aria-label="Executive summary">
                <h2 className={h2}>The short version</h2>
                {(shown.executive ?? []).length === 0 && <p className="text-paper-dim">Nothing to report.</p>}
                <ul className="space-y-3">
                  {(shown.executive ?? []).map((l, i) => {
                    const h = safeDeskHref(l.href)
                    return (
                      <li key={i} className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-3">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className={chip}>{groupLabel(l.group)}</span>
                          <span className={`text-[13px] font-semibold ${DIR_TONE[l.direction]}`}><span aria-hidden="true">{DIR_MARK[l.direction]} </span>{directionLabel(l.direction)}</span>
                        </span>
                        <span className="min-w-0 break-words text-[14px] text-paper">
                          {l.line}{' '}
                          {h && <Link to={h} className="whitespace-nowrap text-amber-glow underline underline-offset-2">{l.measure || 'Open'}</Link>}
                        </span>
                      </li>
                    )
                  })}
                </ul>
                <p className="text-[12px] text-paper-faint">{writtenLine(shown)}</p>
              </section>
              {(shown.detail ?? []).map((s, i) => <Section key={`${shown.id}:${i}`} s={s} />)}
              {shown.recommendations.length > 0 && (
                <div className="space-y-3">
                  <h2 className={h2}>What to do</h2>
                  {shown.recommendations.map((r) => (
                    <RecommendationCard key={r.id} rec={r} onDiscuss={(x) => discuss.mutate(x)}
                      onChanged={() => void qc.invalidateQueries({ queryKey: ['vd-agent-run', shown.id] })} />
                  ))}
                  <ErrorLine text={discuss.isError ? errorDetail(discuss.error) : null} />
                </div>
              )}
            </>
          )}
          {(convs.data?.conversations.length ?? 0) > 0 && (
            <section className={`${card} space-y-2`} aria-label="Conversations">
              <h2 className={h2}>Conversations</h2>
              <ul className="space-y-1">
                {convs.data?.conversations.map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => setConv(c.id)}
                      className={`w-full break-words rounded-lg px-2 py-1.5 text-left text-[14px] hover:bg-ink-600 ${conv === c.id ? 'bg-ink-600 text-paper' : 'text-paper-dim'}`}>
                      {c.title}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
        <div className="min-w-0 md:sticky md:top-4 md:self-start">
          <ChatPanel scope={pub} conversationId={conv} onOpen={setConv} />
        </div>
      </div>
    </div>
  )
}
